import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import AppLayout from '../components/layout/AppLayout';
import { createShopShift, fetchCashierDevices, fetchShopCashiers } from '../services/api';
import type { CashierDevice, ShopCashier } from '../types';
import { deviceOptionLabel, shopDeviceErrorMessage } from '../utils/cashierDevices';
import { SHIFT, deviceStatus } from '@kioskgaming/ui';

function combineDateAndTime(date: string, time: string): string {
  return new Date(`${date}T${time}:00`).toISOString();
}

export default function ShiftFormPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledCashierId = searchParams.get('cashierId') || '';
  const [cashiers, setCashiers] = useState<ShopCashier[]>([]);
  const [devices, setDevices] = useState<CashierDevice[]>([]);
  const [loadingCashiers, setLoadingCashiers] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [devicesUnavailable, setDevicesUnavailable] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [shiftDate, setShiftDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('16:00');
  const [lateGraceMinutes, setLateGraceMinutes] = useState(15);
  const [cashierId, setCashierId] = useState(prefilledCashierId);
  const [deviceId, setDeviceId] = useState('');
  const [note, setNote] = useState('');

  const activeCashiers = useMemo(
    () => cashiers.filter((c) => c.status === 'active'),
    [cashiers]
  );

  const activeDevices = useMemo(
    () => devices.filter((d) => d.status === 'active'),
    [devices]
  );

  const getApiErrorMessage = (e: unknown) => {
    const anyE = e as { response?: { data?: { message?: string } }; message?: string };
    return anyE?.response?.data?.message || anyE?.message || null;
  };

  useEffect(() => {
    const load = async () => {
      setLoadingCashiers(true);
      setLoadingDevices(true);
      // Settled separately: the device list is optional, so a failed device fetch must not block the form.
      const [cashierResult, deviceResult] = await Promise.allSettled([
        fetchShopCashiers(),
        fetchCashierDevices(),
      ]);
      try {
        if (cashierResult.status === 'rejected') throw cashierResult.reason;
        const cashierRes = cashierResult.value;
        if (!cashierRes.success) throw new Error(cashierRes.message || 'Failed to load Cashiers');
        const nextCashiers = cashierRes.data || [];
        setCashiers(nextCashiers);
        if (prefilledCashierId && nextCashiers.some((c) => c.id === prefilledCashierId)) {
          setCashierId(prefilledCashierId);
        }
      } catch (e: unknown) {
        toast.error(getApiErrorMessage(e) || 'Failed to load Cashiers');
      }
      if (deviceResult.status === 'fulfilled' && deviceResult.value.success) {
        setDevices(deviceResult.value.data || []);
        setDevicesUnavailable(false);
      } else {
        setDevices([]);
        setDevicesUnavailable(true);
      }
      setLoadingCashiers(false);
      setLoadingDevices(false);
    };
    void load();
  }, [prefilledCashierId]);

  const selectedCashier = useMemo(
    () => cashiers.find((c) => c.id === cashierId) || null,
    [cashiers, cashierId]
  );

  const backPath = prefilledCashierId
    ? `/cashiers/${encodeURIComponent(prefilledCashierId)}`
    : '/shifts';

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!cashierId) {
      toast.error('Please select a Cashier.');
      return;
    }
    setSubmitting(true);
    try {
      const scheduledStartAt = combineDateAndTime(shiftDate, startTime);
      const scheduledEndAt = combineDateAndTime(shiftDate, endTime);
      if (new Date(scheduledEndAt) <= new Date(scheduledStartAt)) {
        toast.error('End time must be after start time.');
        setSubmitting(false);
        return;
      }
      const res = await createShopShift({
        shiftDate,
        scheduledStartAt,
        scheduledEndAt,
        cashierId,
        deviceId: deviceId || null,
        lateGraceMinutes,
        note: note.trim() || undefined,
      });
      if (!res.success) {
        toast.error(shopDeviceErrorMessage(res, 'Schedule shift failed'));
        return;
      }
      toast.success('Shift created');
      if (res.data?.id) {
        navigate(`/shifts/${encodeURIComponent(res.data.id)}`);
      } else {
        navigate('/shifts');
      }
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Schedule shift failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppLayout contentFullWidth>
      <div className="w-full max-w-none">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-xl font-semibold">{SHIFT.scheduleShift}</h1>
            <p className="mt-0.5 text-sm text-stone-600">
              {selectedCashier
                ? `Schedule a shift for ${selectedCashier.name}.`
                : 'Schedule a Cashier for a specific date and time.'}
            </p>
          </div>
          <button
            type="button"
            className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
            onClick={() => navigate(backPath)}
          >
            {prefilledCashierId ? '← Cashier' : '← Schedule'}
          </button>
        </div>

        <form onSubmit={(e) => void onSubmit(e)} className="mt-4 grid gap-4 lg:grid-cols-3">
          <div className="rounded-xl border border-stone-200 bg-white lg:col-span-2">
            <div className="flex items-center justify-between border-b border-stone-200 px-4 py-3">
              <div>
                <h2 className="text-base font-semibold">Shift information</h2>
                <p className="text-xs text-stone-500">All times use your local timezone.</p>
              </div>
              <span className="rounded-full bg-blue-50 px-2 py-1 text-[11px] font-semibold text-blue-700">
                Draft
              </span>
            </div>
            <div className="grid gap-4 p-4 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-stone-700">Date *</label>
                <input
                  type="date"
                  value={shiftDate}
                  onChange={(e) => setShiftDate(e.target.value)}
                  required
                  className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Late grace *</label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={120}
                    value={lateGraceMinutes}
                    onChange={(e) => setLateGraceMinutes(Number(e.target.value))}
                    className="w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                  />
                  <span className="shrink-0 text-sm text-stone-500">minutes</span>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Scheduled start *</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  required
                  className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Scheduled end *</label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  required
                  className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Assigned Cashier *</label>
                <select
                  value={cashierId}
                  onChange={(e) => setCashierId(e.target.value)}
                  required
                  disabled={loadingCashiers || Boolean(prefilledCashierId)}
                  className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                >
                  <option value="">Select Cashier</option>
                  {activeCashiers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-stone-500">
                  {prefilledCashierId
                    ? 'Cashier was selected from Cashiers.'
                    : 'Only active Cashiers belonging to this Shop are shown.'}
                </p>
              </div>
              <div>
                <label htmlFor="shift-device" className="text-sm font-medium text-stone-700">
                  Device (optional)
                </label>
                <select
                  id="shift-device"
                  value={deviceId}
                  onChange={(e) => setDeviceId(e.target.value)}
                  disabled={loadingDevices}
                  className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                >
                  <option value="">Any approved device (chosen at start)</option>
                  {activeDevices.map((d) => (
                    <option key={d.id} value={d.id}>
                      {deviceOptionLabel(d)}
                    </option>
                  ))}
                </select>
                {loadingDevices ? (
                  <p className="mt-1 text-xs text-stone-500">Loading devices…</p>
                ) : devicesUnavailable ? (
                  <p className="mt-1 text-xs text-amber-700">
                    Devices could not be loaded. You can still save with “Any approved device”.
                  </p>
                ) : deviceId ? (
                  <p className="mt-1 text-xs text-stone-500">The shift can only start on this device.</p>
                ) : (
                  <p className="mt-1 text-xs text-stone-500">
                    The Cashier can start on any approved device of this Shop.
                  </p>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-stone-700">Shift note</label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="Optional operational note, e.g. handover from morning shift"
                  className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                />
              </div>
            </div>
            <div className="rounded-b-xl border-t border-stone-200 bg-stone-50 px-4 py-3">
              <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                <strong>Validate before saving:</strong> The system checks Cashier overlap, and device overlap
                when a device is pinned.
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-stone-200 px-4 py-3">
              <button
                type="button"
                className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
                onClick={() => navigate(backPath)}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !cashierId}
                className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
              >
                {submitting ? 'Saving…' : SHIFT.scheduleShift}
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
              <h3 className="text-sm font-semibold text-blue-950">How devices work</h3>
              <ol className="mt-3 list-decimal space-y-1.5 pl-4 text-sm text-blue-950">
                <li>The Cashier signs in on the counter device.</li>
                <li>
                  When the shift starts, a new device needs your approval under{' '}
                  <button
                    type="button"
                    className="font-semibold underline"
                    onClick={() => navigate('/cashier-devices')}
                  >
                    Devices
                  </button>
                  , or a pairing code.
                </li>
                <li>Pinning a device means the shift can only start there.</li>
              </ol>
            </div>
            <div className="rounded-xl border border-stone-200 bg-white p-4">
              <h3 className="text-sm font-semibold">Release rules</h3>
              <p className="text-xs text-stone-500">Automatic validation</p>
              <div className="mt-3 space-y-2 text-sm">
                {[
                  ['Cashier belongs to Shop', 'Required'],
                  ['Cashier status', 'Active'],
                  ['Device status', deviceStatus('active').label],
                  ['Cashier overlap', 'Blocked'],
                  ['Device overlap', 'Blocked'],
                  ['End after start', 'Required'],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between gap-2 border-b border-stone-100 pb-2">
                    <span className="text-stone-600">{label}</span>
                    <strong className="text-stone-900">{value}</strong>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-stone-200 bg-white p-4">
              <h3 className="text-sm font-semibold">Shop defaults</h3>
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-stone-600">Late grace</span>
                  <strong>15 min</strong>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-stone-600">Closing grace</span>
                  <strong>15 min</strong>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}
